import { Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { capitalizeFirstLetter, type CURRENCYCODETYPE, CURRENCYCONFIG, type IApiResponse, type IVIPStats } from 'BreetHelpers';
import { useCurrencyOutput } from 'BreetHooks';
import { selectFiatMeta, useAppSelector } from 'BreetRedux';
import { memo } from 'react';

import type { ITablePlainColumn } from '../Table/Table.helpers';
import TablePlain from '../Table/TablePlain';
import { ANALYTICS_TABS, type AnalyticsTab } from './Analytics.helpers';

interface IAnalyticsStats {
	activeTab: AnalyticsTab;
	vipStatsData?: IApiResponse<IVIPStats>;
	isLoading?: boolean;
	handleStatsTabClick: (tab: AnalyticsTab) => () => void;
}

interface IWithdrawalStatRow {
	id: string;
	currency: string;
	completed: string;
	processing: string;
	pending: string;
	reversed: string;
}

const WITHDRAWAL_CURRENCIES = [
	{ code: 'NGN', label: 'Total Naira', key: 'ngn' },
	{ code: 'GHS', label: 'Total Cedis', key: 'ghs' },
	{ code: 'USD', label: 'Total USD', key: 'usd' },
] as const satisfies { code: CURRENCYCODETYPE; label: string; key: Lowercase<CURRENCYCODETYPE> }[];

export const AnalyticsStats = memo(({ vipStatsData, handleStatsTabClick, activeTab, isLoading }: IAnalyticsStats) => {
	const fiatMeta = useAppSelector(selectFiatMeta);

	const vipStats = vipStatsData?.data;
	const withdrawStats = vipStats?.withdrawalsAnalytics ?? [];
	const sellStats = vipStats?.sellsAnalytics ?? [];
	const conversionStats = vipStats?.conversionsAnalytics;

	const currencyOutput = useCurrencyOutput();
	const fiatValue = (code: CURRENCYCODETYPE, value?: number) => `${CURRENCYCONFIG[code].symbol}${currencyOutput(value ?? 0)}`;

	const currencyName = capitalizeFirstLetter(fiatMeta.currencyShort);
	const currency = fiatMeta.currency;

	const transactionRows = [
		{ label: 'Total Count', value: sellStats[0]?.totalCount ?? 0 },
		{ label: 'Total USD Amount', value: fiatValue('USD', sellStats[0]?.totalUsdAmount) },
		{ label: `Total ${currencyName} Amount`, value: fiatValue(currency, sellStats[0]?.totalNairaAmount) },
		{ label: 'Total Successful Trades', value: sellStats[0]?.successfulTrades ?? 0 },
		{ label: 'Total Pending Trades', value: sellStats[0]?.pendingTrades ?? 0 },
		{ label: 'Total Unsuccessful Trades', value: sellStats[0]?.unsucessfulTrades ?? 0 },
	];

	const withdrawalTableData: IWithdrawalStatRow[] = WITHDRAWAL_CURRENCIES.map(({ code, label, key }, index) => {
		const currencyStats = withdrawStats[0]?.[key];

		return {
			id: String(index),
			currency: label,
			completed: fiatValue(code, currencyStats?.completedAmount),
			processing: fiatValue(code, currencyStats?.processingAmount),
			pending: fiatValue(code, currencyStats?.pendingAmount),
			reversed: fiatValue(code, currencyStats?.reversedAmount),
		};
	});

	const withdrawalColumns: ITablePlainColumn<IWithdrawalStatRow>[] = [
		{ title: '', render: ({ row }) => row.currency },
		{ title: 'Completed', render: ({ row }) => row.completed },
		{ title: 'Processing', render: ({ row }) => row.processing },
		{ title: 'Pending', render: ({ row }) => row.pending },
		{ title: 'Reversed', render: ({ row }) => row.reversed },
	];

	const sellsRows = [
		{ label: 'Total USD', value: fiatValue('USD', conversionStats?.usd?.totalAmount ?? 0) },
		{ label: 'Total USD Count', value: conversionStats?.usd?.totalCount ?? 0 },
		{
			label: `Total ${fiatMeta.currency}`,
			value: fiatValue(
				fiatMeta.currency,
				conversionStats?.[fiatMeta.currency.toLowerCase() as Lowercase<CURRENCYCODETYPE>]?.totalAmount ?? 0
			),
		},
		{
			label: `Total ${fiatMeta.currency} Count`,
			value: conversionStats?.[fiatMeta.currency.toLowerCase() as Lowercase<CURRENCYCODETYPE>]?.totalCount ?? 0,
		},
	];

	const listRowsByTab: Record<Exclude<AnalyticsTab, 'Withdrawals'>, { label: string; value: number | string }[]> = {
		Transactions: transactionRows,
		Conversions: sellsRows,
	};

	const activeRows = activeTab === 'Withdrawals' ? [] : listRowsByTab[activeTab];

	return (
		<>
			<div className='appanalytics_subtabs'>
				{ANALYTICS_TABS.map((tab) => (
					<button
						key={tab}
						type='button'
						className={`appanalytics_subtab${activeTab === tab ? ' active bg-primary-400 border-primary-400 color-neutral-50' : ' color-primary-300'}`}
						onClick={handleStatsTabClick(tab)}
					>
						{tab}
					</button>
				))}
			</div>

			{activeTab === 'Withdrawals' ? (
				<div className='appanalytics_wtable'>
					<TablePlain
						columns={withdrawalColumns}
						data={withdrawalTableData}
						skeletonRows={WITHDRAWAL_CURRENCIES.length}
						isLoading={isLoading}
					/>
				</div>
			) : (
				<div className='appanalytics_statslist'>
					{activeRows.map(({ label, value }, index) => (
						<div
							key={label}
							className={classNameMerger(
								'appanalytics_statsrow border-bottom-primary-400',
								index === activeRows.length - 1 ? 'last-child' : ''
							)}
						>
							<Typography className='appanalytics_statsrow_label color-primary-100'>{label}</Typography>
							<Typography className='appanalytics_statsrow_value color-neutral-50'>{String(value)}</Typography>
						</div>
					))}
				</div>
			)}
		</>
	);
});

AnalyticsStats.displayName = 'AnalyticsStats';
